/**
 * Data findings verified against production Postgres on 2026-08-28 (see Task 4 Step 1,
 * 60 most recent `estimate_public_share` rows):
 *
 * 1. `auto_valuations.valuation_summary` is null in every row, and `log_valuation` and
 *    `valuations` are empty arrays. The valuation figures do NOT come from there — the
 *    original plan would have emitted a payload with no valuation at all.
 * 2. The real figures live at the top level, in `price`, keyed by AVM provider
 *    (`Popety.io`, `PriceHubble`, `Wüest Partner`, `manual`), each an object with a
 *    numeric `price`, a `valueRange: { lower, upper }`, and a `confidence`.
 * 3. Popety.io's `confidence` is numeric 1-3; the third parties use strings. Direction
 *    confirmed from the data: confidence 3 carries a ~±12% value range, confidence 1
 *    a ~±23% range, so 3 = high, 2 = medium, 1 = low.
 * 4. No rent figure is present in the stored share at all — `rent_chf_monthly` is null
 *    in every row of V1. There is no monthly-vs-annual ambiguity to resolve because
 *    there is nothing to emit. Do not synthesise one; if a rent is added later, its
 *    unit must be verified against the data before it is labelled.
 */
import type {
  AiHandoffEstimatePayload,
  AiHandoffFigure,
  EstimatePublicShare,
  EstimateSectionId,
} from '@popety-io/types'
import { buildEstimateOverlay } from './estimateOverlay'
import { buildSections, emptySections, SPEC_SECTION_IDS } from './sectionValues'
import { AI_HANDOFF_ABOUT } from './about'
import { AI_HANDOFF_CONNECT_URL } from './constants'
import {
  buildValuationModel,
  COMPARING_MODELS,
  PROVIDER_ORDER,
} from './providers'

/** Sections whose data is typed well enough to emit in V1. */
// `listings_comparables` and `transactions` come through SPEC_SECTION_IDS: they
// hold only what the user saved on the estimate, which was nothing in all 105
// shares audited, so they usually land in `sections_empty`.
const SUPPORTED_SECTIONS: EstimateSectionId[] = [
  'summary',
  'property_details',
  'auto_valuations',
  'manual_valuations',
  // Everything with a declared field map in sectionFields.ts. Adding a spec
  // there makes the section supported here automatically, so the two can never
  // drift into claiming coverage that is not rendered.
  ...SPEC_SECTION_IDS,
]

/**
 * Every recognised section id, mirroring the `EstimateSectionId` union in
 * `@popety-io/types`. `sections_omitted` is the complement of
 * `SUPPORTED_SECTIONS` against the *requested* sections, which — unlike
 * `sections_included` — come straight from the database with no validation
 * on write (`createOrUpdate` stores `req.body.sections` verbatim). Filtering
 * `omitted` against this set stops an arbitrary stored string (e.g. injected
 * Markdown) from reaching the rendered document as a "section id".
 */
const ALL_SECTION_IDS: EstimateSectionId[] = [
  'summary',
  'property_details',
  'history',
  'land',
  'building',
  'energy',
  'taxes',
  'electricity',
  'auto_valuations',
  'manual_valuations',
  'listings_comparables',
  'transactions',
  'purchase_market',
  'rental_market',
  'building_permits',
  'transport_access',
  'amenities',
  'noise_analysis',
  'sunlight',
  'demographics',
  'employment',
  'notes',
  'files',
]

type Band = 'high' | 'medium' | 'low' | 'unknown'

/**
 * Popety.io stores confidence as 1-3. Direction verified against production: a
 * confidence of 3 carries a ~±12% value range and a confidence of 1 a ~±23% range,
 * so 3 is the most confident. Anything else becomes 'unknown' rather than a guess.
 */
const toBand = (v: unknown): Band => {
  if (v === 3) return 'high'
  if (v === 2) return 'medium'
  if (v === 1) return 'low'
  return 'unknown'
}

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

const str = (v: unknown): string | null =>
  typeof v === 'string' && v ? v : null

const avm = (
  v: unknown,
  unit: AiHandoffFigure['unit'],
  asOf: string,
  confidence: Band
): AiHandoffFigure | null => {
  const value = num(v)
  return value === null
    ? null
    : { value, unit, source: 'avm', confidence, as_of: asOf }
}

export const buildEstimateAiPayload = (
  share: EstimatePublicShare,
  opts: { publicUrl: string; asOf: string }
): AiHandoffEstimatePayload => {
  const { asOf, publicUrl } = opts
  const value = (share.value ?? {}) as Record<string, any>
  const summary = (value.summary ?? {}) as Record<string, unknown>
  // Popety.io's own AVM is the headline. PriceHubble and Wüest Partner are
  // emitted alongside it under `valuation.models`, never averaged in (decided
  // 2026-08-28, confirmed by Thibault 2026-10-24).
  const own = ((value.price ?? {})['Popety.io'] ?? {}) as Record<
    string,
    unknown
  >
  const ownRange = (own.valueRange ?? {}) as Record<string, unknown>

  const requested = (share.sections ?? []) as EstimateSectionId[]
  // `requested` comes straight from the database with no validation on write —
  // constrain both included and omitted to the known vocabulary so an arbitrary
  // stored string can never surface in the rendered document as a "section id".
  const recognised = requested.filter((s) => ALL_SECTION_IDS.includes(s))
  const included = recognised.filter((s) => SUPPORTED_SECTIONS.includes(s))
  const omitted = recognised.filter((s) => !SUPPORTED_SECTIONS.includes(s))

  const band = toBand(own.confidence)
  const coords = summary.geo_center as
    { lat?: unknown; lon?: unknown } | undefined
  const lat = num(coords?.lat)
  const lon = num(coords?.lon)

  // `EstimateSummaryData` has no land_area — it lives on the property_details
  // section as landBuilding.landArea. Read the summary first for forward
  // compatibility, then fall back, so the field is not permanently null.
  const landBuilding = (value.property_details?.landBuilding ?? {}) as Record<
    string,
    unknown
  >
  // Track WHERE it came from: the summary value is register/geoportal data,
  // but the property_details fallback is what the customer typed. Labelling
  // customer input as `geoportal` claims a federal source for it.
  const summaryLandArea = num(summary.land_area)
  const landArea = summaryLandArea ?? num(landBuilding.landArea)
  const landAreaSource: AiHandoffFigure['source'] =
    summaryLandArea !== null ? 'geoportal' : 'user'

  const sections = buildSections(value, included, asOf)

  return {
    schema_version: '1.0',
    about: AI_HANDOFF_ABOUT,
    artifact: 'estimate',
    as_of: asOf,
    attribution: {
      source: 'Popety.io',
      url: publicUrl,
      connect_url: AI_HANDOFF_CONNECT_URL,
    },
    sections_included: included,
    sections_omitted: omitted,
    sections_empty: emptySections(included, sections),
    // `identity` is deliberately NOT gated on `included.includes('summary')`, unlike
    // `valuation` below. A payload with no address, EGID or municipality identifies
    // no property — the assistant would be reasoning about an anonymous valuation,
    // which is less safe than naming the subject, not more. Gating it would also be
    // ineffective: `value.header` (title + address) is written unconditionally by
    // the share builder regardless of section selection, and `egid`/`address` are
    // already read from it as a fallback above. `valuation` IS gated because `price`
    // genuinely over-collects: verified in production, it is present in 6 of 118
    // rows whose share did not select `auto_valuations`.
    //
    // `egid` and `egrid` are structurally null for every stored share today: neither
    // `summary` nor `value.header` is ever written with those keys (verified across
    // production `estimate_public_share` rows — 0/60 carry either). This is the same
    // "nothing to source" situation as `rent_chf_monthly` below — do not read this
    // type and assume a value is coming through; it is not, until something upstream
    // starts writing it onto the share.
    identity: {
      egid: num(summary.egid ?? value.header?.egid),
      egrid: str(summary.egrid ?? value.header?.egrid),
      address_full: str(
        summary.address_full ?? summary.address ?? value.header?.address
      ),
      municipality_name: str(summary.municipality_name),
      region_name: str(summary.region_name),
      coordinates: lat !== null && lon !== null ? { lat, lon } : null,
      property_category: str(summary.property_type),
      building_year: num(summary.construction_year),
      land_area:
        landArea === null
          ? null
          : {
              value: landArea,
              unit: 'm2',
              source: landAreaSource,
              as_of: asOf,
            },
      unit_nb: num(summary.unit_nb),
    },
    valuation: included.includes('auto_valuations')
      ? {
          title: 'Auto valuations',
          headline: {
            provider: 'Popety.io',
            value_chf: avm(own.price, 'CHF', asOf, band),
            value_chf_lower: avm(ownRange.lower, 'CHF', asOf, band),
            value_chf_upper: avm(ownRange.upper, 'CHF', asOf, band),
            confidence_band: band,
            why_this_one:
              "Popety.io's own model. The other providers are listed under " +
              'models; they are not averaged into this figure.',
          },
          models: PROVIDER_ORDER.map((key) =>
            buildValuationModel(key, (value.price ?? {})[key], asOf)
          ).filter((m): m is NonNullable<typeof m> => m !== null),
          comparing_models: COMPARING_MODELS,
          // No rent is stored on a share. Stated rather than silently absent.
          rent_chf_monthly: null,
        }
      : null,
    overlay: buildEstimateOverlay(share.value, asOf, included),
    sections,
  }
}
